/**
 * Storage seguro para informações de autenticação.
 * Usa sessionStorage por padrão (mais seguro que localStorage).
 * Os dados são armazenados de forma estruturada.
 */

import type { AuthUser } from './endpoints/auth';

export interface AuthStorage {
  saveAuthData: (authData: AuthUser) => void;
  getAuthData: () => AuthUser | null;
  clearAuthData: () => void;
  getToken: () => string | null;
  getRefreshToken: () => string | null;
  getUserId: () => string | null;
  getEmail: () => string | null;
  getRoles: () => string[];
  isAdmin: () => boolean;
  isTenantAdmin: () => boolean;
}

const DEFAULT_AUTH_KEY = 'auth_data';

/**
 * Cria um storage de autenticação usando sessionStorage (mais seguro).
 * SessionStorage é limpo quando a aba/janela é fechada.
 * 
 * Para usar localStorage (persiste entre sessões), passe useLocalStorage: true
 */
export function createAuthStorage(
  authKey = DEFAULT_AUTH_KEY,
  useLocalStorage = false
): AuthStorage {
  const storage = useLocalStorage ? localStorage : sessionStorage;

  return {
    saveAuthData: (authData: AuthUser) => {
      try {
        const dataToStore = {
          token: authData.token,
          email: authData.email,
          userId: authData.userId,
          expiresAt: authData.expiresAt,
          roles: authData.roles,
          isGeneralAdmin: authData.isGeneralAdmin,
          refreshToken: authData.refreshToken,
          isTenantUserAdmin: authData.isTenantUserAdmin,
          storedAt: new Date().toISOString(),
        };
        storage.setItem(authKey, JSON.stringify(dataToStore));
      } catch (error) {
        console.error('Failed to save auth data:', error);
      }
    },

    getAuthData: () => {
      try {
        const data = storage.getItem(authKey);
        if (!data) return null;

        const parsed = JSON.parse(data) as AuthUser & { storedAt: string };
        
        // Verifica se o token expirou
        const expiresAt = new Date(parsed.expiresAt);
        const now = new Date();
        
        if (now >= expiresAt) {
          storage.removeItem(authKey);
          return null;
        }

        return parsed;
      } catch (error) {
        console.error('Failed to load auth data:', error);
        return null;
      }
    },

    clearAuthData: () => {
      try {
        storage.removeItem(authKey);
      } catch (error) {
        console.error('Failed to clear auth data:', error);
      }
    },

    getToken: () => {
      const authData = createAuthStorage(authKey, useLocalStorage).getAuthData();
      return authData?.token ?? null;
    },

    getRefreshToken: () => {
      const authData = createAuthStorage(authKey, useLocalStorage).getAuthData();
      return authData?.refreshToken ?? null;
    },

    getUserId: () => {
      const authData = createAuthStorage(authKey, useLocalStorage).getAuthData();
      return authData?.userId ?? null;
    },

    getEmail: () => {
      const authData = createAuthStorage(authKey, useLocalStorage).getAuthData();
      return authData?.email ?? null;
    },

    getRoles: () => {
      const authData = createAuthStorage(authKey, useLocalStorage).getAuthData();
      return authData?.roles ?? [];
    },

    isAdmin: () => {
      const authData = createAuthStorage(authKey, useLocalStorage).getAuthData();
      return authData?.isGeneralAdmin ?? false;
    },

    isTenantAdmin: () => {
      const authData = createAuthStorage(authKey, useLocalStorage).getAuthData();
      return authData?.isTenantUserAdmin ?? false;
    },
  };
}

/**
 * Cria um storage de autenticação usando localStorage (persiste entre sessões).
 * Menos seguro que sessionStorage, mas mantém o usuário logado.
 */
export function createPersistentAuthStorage(
  authKey = DEFAULT_AUTH_KEY
): AuthStorage {
  return createAuthStorage(authKey, true);
}
